import React from 'react';
import { motion } from 'framer-motion';
import { 
  Leaf, 
  Recycle, 
  Zap, 
  Globe, 
  ShieldCheck, 
  Droplets, 
  Wind, 
  Factory 
} from 'lucide-react';
import { IMAGES } from '@/assets/images';
import { ROUTE_PATHS } from '@/lib/index';
import { springPresets, fadeInUp, staggerContainer, staggerItem } from '@/lib/motion';

/**
 * Página de Sostenibilidad
 * Enfocada en iniciativas ambientales, tecnología verde y responsabilidad social corporativa.
 */
export default function SustainabilityEs() {
  const sustainabilityPillars = [
    {
      icon: <Recycle className="w-10 h-10 text-primary" />,
      title: "Economía Circular",
      description: "El aluminio es un metal 100% reciclable. En nuestros procesos de producción, protegemos los recursos naturales elevando la recuperación de chatarra al 95%.",
    },
    {
      icon: <Zap className="w-10 h-10 text-primary" />,
      title: "Eficiencia Energética",
      description: "Con los sistemas de automatización inteligente que utilizamos en nuestras líneas de extrusión y hornos, hemos reducido el consumo de energía en un 30% según nuestros objetivos de 2026.",
    },
    {
      icon: <Wind className="w-10 h-10 text-primary" />,
      title: "Huella de Carbono",
      description: "Minimizamos nuestras emisiones de carbono con nuestra cadena de suministro certificada de energía verde y los paneles solares en los techos de nuestras fábricas.",
    },
    {
      icon: <Droplets className="w-10 h-10 text-primary" />,
      title: "Gestión del Agua",
      description: "Nuestro objetivo es reducir a cero el desperdicio de agua utilizando sistemas de tratamiento de agua de circuito cerrado en nuestras instalaciones de tratamiento superficial y fundición.",
    },
  ];

  const initiatives = [
    {
      title: "Reciclaje y Fundición",
      content: "Una gran parte de los billetes de aluminio que procesamos en nuestra propia fundición se obtiene mediante el reprocesamiento de chatarra de alta calidad recolectada de nuestra fábrica y del mercado. Este proceso requiere un 95% menos de energía que la producción de aluminio primario.",
      image: IMAGES.SUSTAINABILITY_3,
    },
  ];

  return (
    <div className="flex flex-col w-full">
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden bg-primary">
        <img 
          src={IMAGES.SUSTAINABILITY_1} 
          alt="Sostenibilidad" 
          className="absolute inset-0 object-cover w-full h-full opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-primary/60 via-transparent to-background" />
        
        <div className="container relative z-10 mx-auto px-4 text-center">
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springPresets.gentle}
            className="text-4xl md:text-6xl font-bold text-white mb-6"
          >
            Soluciones <span className="text-accent">Sostenibles</span> para el Futuro
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springPresets.gentle, delay: 0.2 }}
            className="text-xl text-white/90 max-w-3xl mx-auto"
          >
            Con tecnologías de producción respetuosas con la naturaleza y nuestro modelo de economía circular, transformamos el poder del aluminio para el mañana.
          </motion.p>
        </div>
      </section>

      {/* Core Pillars Section */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {sustainabilityPillars.map((pillar, index) => (
              <motion.div
                key={index}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ delay: index * 0.1 }}
                className="p-8 bg-card border border-border rounded-xl shadow-sm hover:shadow-md transition-all group"
              >
                <div className="mb-6 transform group-hover:scale-110 transition-transform duration-300">
                  {pillar.icon}
                </div>
                <h3 className="text-xl font-bold mb-4">{pillar.title}</h3>
                <p className="text-muted-foreground leading-relaxed">
                  {pillar.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Initiatives */}
      <section className="py-24 bg-secondary/30">
        <div className="container mx-auto px-4">
          <div className="flex flex-col space-y-24">
            {initiatives.map((item, index) => (
              <div 
                key={index} 
                className={`flex flex-col lg:flex-row items-center gap-12 ${index % 2 !== 0 ? 'lg:flex-row-reverse' : ''}`}
              >
                <motion.div 
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  className="flex-1"
                >
                  <h2 className="text-3xl font-bold mb-6">{item.title}</h2>
                  <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                    {item.content}
                  </p>
                  <div className="flex items-center gap-4 text-primary font-semibold">
                    <ShieldCheck className="w-6 h-6" />
                    <span>Cumplimiento Total con Estándares ISO 14001 y LEED</span>
                  </div>
                </motion.div>
                <motion.div 
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  className="flex-1 w-full"
                >
                  <div className="aspect-video rounded-2xl overflow-hidden shadow-2xl border-4 border-white">
                    <img 
                      src={item.image} 
                      alt={item.title} 
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                </motion.div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CSR & Community Section */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="bg-primary rounded-3xl p-12 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 p-8 opacity-10">
              <Globe className="w-64 h-64" />
            </div>
            <div className="relative z-10 max-w-2xl">
              <h2 className="text-3xl font-bold mb-6">Responsabilidad Social Corporativa</h2>
              <p className="text-lg text-white/80 mb-8">
                No solo producimos metal; invertimos en el futuro de nuestra comunidad y nuestros empleados. Agregamos valor a nuestro ecosistema con proyectos educativos locales, becas de desarrollo profesional y actividades de forestación.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                <div className="flex flex-col">
                  <span className="text-3xl font-bold text-accent">50.000+</span>
                  <span className="text-sm text-white/60">Árboles Plantados</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-3xl font-bold text-accent">100+</span>
                  <span className="text-sm text-white/60">Becas Educativas</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-3xl font-bold text-accent">%100</span>
                  <span className="text-sm text-white/60">Enfoque en Seguridad Laboral</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-accent/5 border-y border-border">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <Leaf className="w-12 h-12 text-primary mx-auto mb-6" />
            <h2 className="text-3xl font-bold mb-4">Examine Nuestros Certificados de Producción Verde</h2>
            <p className="text-muted-foreground mb-8 text-lg">
              Puede visitar nuestro centro de documentos para descargar nuestros informes de sostenibilidad y declaraciones de impacto ambiental (EPD).
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a 
                href={ROUTE_PATHS.DOWNLOAD_CENTER_ES} 
                className="px-8 py-3 bg-primary text-white font-bold rounded-lg hover:bg-primary/90 transition-colors"
              >
                Centro de Documentos
              </a>
              <a 
                href={ROUTE_PATHS.CONTACT_ES} 
                className="px-8 py-3 bg-white border border-primary text-primary font-bold rounded-lg hover:bg-secondary transition-colors"
              >
                Obtener Información Detallada
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}